import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  MemberCountry,
  CreateMemberCountry,
  UpdateMemberCountry,
  UpdateMemberCountryStatus,
  MemberCountryFilterParams,
  MemberCountryStatsResponse,
  MemberCountryRegionStatsResponse
} from '@/lib/api/types'
import { MemberCountryStatsResponseSchema, MemberCountryRegionStatsResponseSchema } from '@/lib/api/types'

export const memberCountryService = {
  // Filter member countries
  filterMemberCountries: async (params: PaginationParams, filters?: MemberCountryFilterParams): Promise<ApiResponse<PaginatedResponse<MemberCountry>>> => {
    return await post<ApiResponse<PaginatedResponse<MemberCountry>>>(
      'member-countries/private/filter',
      { ...params, ...filters }
    )
  },

  getAllMemberCountries: async (): Promise<ApiResponse<MemberCountry[]>> => {
    return await get<ApiResponse<MemberCountry[]>>(
      'member-countries/private'
    )
  },

  // Get member country by ID
  getMemberCountryById: async (memberCountryId: string): Promise<ApiResponse<MemberCountry>> => {
    return await get<ApiResponse<MemberCountry>>(
      `member-countries/private/${memberCountryId}`
    )
  },

  // Create a new member country
  createMemberCountry: async (data: CreateMemberCountry): Promise<ApiResponse<MemberCountry>> => {
    return await post<ApiResponse<MemberCountry>>(
      'member-countries/admin/private',
      data
    )
  },

  // Update an existing member country
  updateMemberCountry: async (
    memberCountryId: string,
    data: UpdateMemberCountry
  ): Promise<ApiResponse<MemberCountry>> => {
    return await put<ApiResponse<MemberCountry>>(
      `member-countries/admin/private/${memberCountryId}`,
      data
    )
  },

  // Update member country status
  updateMemberCountryStatus: async (
    memberCountryId: string,
    data: UpdateMemberCountryStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `member-countries/admin/private/${memberCountryId}/status`,
      data
    )
  },

  // Delete a member country (uses POST with body)
  deleteMemberCountry: async (memberCountryId: string ): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `member-countries/admin/private/${memberCountryId}`
    )
  },

  // Get member countries stats
  getMemberCountriesStats: async (): Promise<MemberCountryStatsResponse> => {
    const res = await get('member-countries/private/stats')
    return MemberCountryStatsResponseSchema.parse(res)
  },

  // Get region-specific member country stats
  getMemberCountryRegionStats: async (regionId: string): Promise<MemberCountryRegionStatsResponse> => {
    const res = await get(`member-countries/private/regions/${regionId}/stats`)
    return MemberCountryRegionStatsResponseSchema.parse(res)
  },

  // Search member countries
  searchMemberCountries: async (
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<MemberCountry>>> => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      size: params.size.toString()
    })
    
    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<MemberCountry>>>(
      `member-countries/private/search?${queryParams.toString()}`
    )
  }
}
